import { useState } from 'react';
import type { FutebolPartidaAtiva, FutebolJogoDia } from '@/shared/types';

interface Props {
  partida?: FutebolPartidaAtiva | null;
  jogo?:    FutebolJogoDia | null;
}

const TOTAL = 20;
const CORES = ['#009c3b', '#FFDF00'];

function Escudinho({ src, alt }: { src: string | null | undefined; alt: string }) {
  const [err, setErr] = useState(false);
  if (src && !err) {
    return (
      <img
        src={src}
        alt={alt}
        onError={() => setErr(true)}
        className="w-5 h-5 object-contain flex-shrink-0"
      />
    );
  }
  return (
    <span
      className="w-5 h-5 rounded-full bg-white/10 flex items-center justify-center text-[7px] font-black text-white/50 uppercase flex-shrink-0"
    >
      {alt.slice(0, 2)}
    </span>
  );
}

function isBrasilTime(nome: string) {
  return nome.toLowerCase().includes('brasil');
}

// ── Sub-banner: placar ao vivo ────────────────────────────────────────────────
function BannerPlacar({ partida, jogo }: { partida: FutebolPartidaAtiva; jogo: FutebolJogoDia }) {
  const nomeCasa      = jogo.time_casa;
  const nomeVisitante = jogo.time_visitante;
  const emAndamento   = jogo.status === 'em_andamento';

  return (
    <div className="animate-fade-in overflow-hidden relative">
      <div className="bg-[linear-gradient(to_bottom,rgba(0,80,30,0.28)_0%,rgba(0,156,59,0.06)_60%,transparent_100%)] border-b border-green-600/[0.20] px-4 py-2">
        <div className="flex items-center justify-between gap-2">

          {/* Time da casa */}
          <div className="flex items-center gap-1.5 min-w-0 flex-1">
            <Escudinho src={jogo.escudo_casa} alt={nomeCasa} />
            <span className="text-[10px] font-semibold text-white/70 truncate max-w-[70px]">
              {isBrasilTime(nomeCasa) ? '🇧🇷' : ''} {nomeCasa}
            </span>
          </div>

          {/* Placar central */}
          <div className="flex flex-col items-center flex-shrink-0">
            <div className="flex items-center gap-1.5">
              <span className="font-black text-white tabular-nums text-base leading-none">
                {partida.placar_casa}
              </span>
              <span className="text-white/30 text-xs">×</span>
              <span className="font-black text-white tabular-nums text-base leading-none">
                {partida.placar_visitante}
              </span>
            </div>
            {emAndamento && (
              <div className="flex items-center gap-1 mt-0.5">
                <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse flex-shrink-0" />
                <span className="text-[8px] font-bold uppercase tracking-wider text-red-400">
                  {partida.tempo_jogo && /^\d+$/.test(partida.tempo_jogo)
                    ? `${partida.tempo_jogo}'`
                    : partida.tempo_jogo === 'HT' ? 'Intervalo'
                    : 'Ao vivo'}
                </span>
              </div>
            )}
          </div>

          {/* Time visitante */}
          <div className="flex items-center gap-1.5 min-w-0 flex-1 justify-end">
            <span className="text-[10px] font-semibold text-white/70 truncate max-w-[70px] text-right">
              {nomeVisitante} {isBrasilTime(nomeVisitante) ? '🇧🇷' : ''}
            </span>
            <Escudinho src={jogo.escudo_visitante} alt={nomeVisitante} />
          </div>

        </div>
      </div>
    </div>
  );
}

// ── Banner padrão: bolinhas + "Vamos Brasil" ──────────────────────────────────
function BannerPadrao() {
  return (
    <div className="animate-fade-in overflow-hidden relative">
      <div className="bg-[linear-gradient(to_bottom,rgba(0,80,30,0.22)_0%,rgba(0,156,59,0.05)_60%,transparent_100%)] border-b border-green-600/[0.20] pb-2">
        <div className="relative pt-0">
          <div className="absolute top-2.5 inset-x-0 h-px bg-[linear-gradient(to_right,transparent,rgba(0,156,59,0.5)_10%,rgba(255,223,0,0.5)_50%,rgba(0,156,59,0.5)_90%,transparent)]" />
          <svg
            width="100%"
            height="24"
            viewBox={`0 0 ${TOTAL * 18} 24`}
            preserveAspectRatio="none"
            aria-hidden
          >
            {Array.from({ length: TOTAL }, (_, i) => {
              const cor = CORES[i % CORES.length];
              const cx  = i * 18 + 9;
              return (
                <circle
                  key={i}
                  cx={cx}
                  cy={10}
                  r={5}
                  fill={cor}
                  opacity={0.85}
                  className="animate-junina-balanco"
                  style={{ transformOrigin: `${cx}px 2px`, animationDelay: `${i * 70}ms` }}
                />
              );
            })}
          </svg>
        </div>
        <div className="flex items-center justify-center gap-1.5 pt-0.5">
          <p
            className="font-black uppercase text-center text-[10px] tracking-[0.22em]"
            style={{ color: '#FFDF00', textShadow: '0 0 10px rgba(0,156,59,0.5)' }}
          >
            Vamos Brasil
          </p>
          <span className="text-[10px]">⚽</span>
        </div>
      </div>
    </div>
  );
}

// ── Componente principal ──────────────────────────────────────────────────────
export default function JukeboxCopaBanner({ partida, jogo }: Props) {
  if (partida?.fixture_id && jogo) {
    return <BannerPlacar partida={partida} jogo={jogo} />;
  }
  return <BannerPadrao />;
}
